import { apiClient } from '../utils/api-client'
import type { ExerciseTemplate, ExerciseTemplatePayload } from '../types/exercise-template'

interface TemplateResponse {
  data: { id: string; type: string; attributes: ExerciseTemplate }
}

interface TemplatesResponse {
  data: Array<{ id: string; type: string; attributes: ExerciseTemplate }>
}

function unwrap(entry: { id: string; attributes: ExerciseTemplate }): ExerciseTemplate {
  return { ...entry.attributes, id: entry.id }
}

/**
 * One block of a custom exercise at a time. The editor saves each block on its
 * own — the exercise-level `templates` payload replaces the whole set, which is
 * right for creating and wrong for editing.
 */
class ExerciseTemplatesService {
  async create(exerciseId: string, payload: ExerciseTemplatePayload): Promise<ExerciseTemplate> {
    const response = await apiClient.post(`/api/exercises/${exerciseId}/exercise_templates`, {
      data: { attributes: payload },
    }, { requireAuth: true })
    const json: TemplateResponse = await response.json()
    return unwrap(json.data)
  }

  async update(
    exerciseId: string,
    id: string,
    payload: Partial<ExerciseTemplatePayload>
  ): Promise<ExerciseTemplate> {
    const response = await apiClient.patch(`/api/exercises/${exerciseId}/exercise_templates/${id}`, {
      data: { attributes: payload },
    }, { requireAuth: true })
    const json: TemplateResponse = await response.json()
    return unwrap(json.data)
  }

  async destroy(exerciseId: string, id: string): Promise<void> {
    await apiClient.delete(`/api/exercises/${exerciseId}/exercise_templates/${id}`, {
      requireAuth: true,
    })
  }

  /** Sends the whole running order; the server renumbers it in one transaction. */
  async reorder(exerciseId: string, ids: string[]): Promise<ExerciseTemplate[]> {
    const response = await apiClient.patch(
      `/api/exercises/${exerciseId}/exercise_templates/reorder`,
      { data: { attributes: { ids } } },
      { requireAuth: true }
    )
    const json: TemplatesResponse = await response.json()
    return json.data.map(unwrap)
  }
}

export const exerciseTemplatesService = new ExerciseTemplatesService()
